import React, { useEffect } from 'react'
import { getErrorComponent, decodeBase64Url } from './error-registry'


/**
 * Error page orchestrator component.
 * Parses query parameters and renders the appropriate error component.
 *
 * @param {Object} props
 * @param {Object} props.history - React Router history object
 */
export const ErrorPage = (props) => {
  useEffect(() => {
    document.title = 'Error - CJ Affiliate'
  }, [])

  const params = new URLSearchParams(window.location.search)
  const errorCode = sanitizeQueryParam(params.get('error'), MAX_ERROR_CODE_LENGTH)
  const errorDescription = sanitizeQueryParam(params.get('error_description'), MAX_ERROR_DESCRIPTION_LENGTH)

  if (shouldRedirectToNewErrorPage(errorCode, errorDescription)) {
    return redirectToNewErrorPage(params)
  }

  const contextData = parseContextData(errorDescription)
  const ErrorComponent = getErrorComponent(errorCode, errorDescription)

  return <ErrorComponent history={props.history} data={contextData} />
}

const cjPublicUrl = 'https://public.cj.com'

const redirectableErrorDescriptions = [
  'federated_user_unauthorized',
  'unverified_email::',
  'lms_service_unavailable',
  'lms_account_not_found',
  'lms_account_data_invalid',
  'user_not_registered',
]

function shouldRedirectToNewErrorPage(errorCode, errorDescription) {
  if (!errorDescription) return false
  return redirectableErrorDescriptions.some(redirectable => errorDescription.includes(redirectable))
}

function redirectToNewErrorPage(params) {
  const destination = new URL('/auth/error', cjPublicUrl)
  destination.search = params.toString()
  window.location.href = destination.href
  return null
}

/**
 * Maximum safe lengths to prevent DoS attacks via oversized query parameters.
 */
const MAX_ERROR_CODE_LENGTH = 200
const MAX_ERROR_DESCRIPTION_LENGTH = 1000

/**
 * Sanitizes query parameter values to prevent XSS and DoS attacks.
 * Defense-in-depth: React escapes output by default, but this adds protection
 * against future XSS if dangerouslySetInnerHTML is added, and prevents DoS
 * via extremely long parameters.
 *
 * @param {string} value - Raw query parameter value
 * @param {number} maxLength - Maximum allowed length
 * @returns {string|null} Sanitized value or null
 */
function sanitizeQueryParam(value, maxLength) {
  if (!value) return null

  // Truncate to prevent DoS
  const truncated = String(value).slice(0, maxLength)

  // Remove potentially dangerous characters (defense-in-depth)
  return truncated.replace(/[<>'"]/g, '')
}


/**
 * Parses context data from error description if it contains encoded data.
 * Format: "prefix::base64EncodedJson"
 *
 * @param {string} errorDescription - Error description from query parameter
 * @returns {Object} Parsed data object, or empty object if parsing fails
 */
function parseContextData(errorDescription) {
  if (!errorDescription || !errorDescription.includes('::')) {
    return {}
  }

  try {
    const [, encodedData] = errorDescription.split('::')
    const decodedData = decodeBase64Url(encodedData)
    return JSON.parse(decodedData)
  } catch (error) {
    console.error('Failed to parse context data from error description:', error)
    return {}
  }
}
